import { questionApi } from '@/lib/api/questionnaireApi';
import { handleQueryError } from '@/lib/queryErrorHandler';
import { QuestionFormData } from '@/lib/questionSchema';
import { QuestionProps } from '@/types/questionnaire';
import { router } from '@inertiajs/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface UseSmartQuestionUpdateProps {
  question: QuestionProps;
  allQuestions: Array<{ id: number; order: number; [key: string]: any }>;
  onClose: () => void;
}

export function useSmartQuestionUpdate({
  question,
  allQuestions,
  onClose,
}: UseSmartQuestionUpdateProps) {
  const queryClient = useQueryClient();

  const updateQuestionMutation = useMutation({
    mutationFn: async (data: QuestionFormData) => {
      // Check if order changed
      if (data.order && data.order !== question.order) {
        // Calculate which questions need to be reordered
        const newOrder = data.order;
        const oldOrder = question.order;

        const reorderData: Array<{ id: number; order: number }> = [];

        // Add the question being updated
        reorderData.push({ id: question.id, order: newOrder });

        // Find questions that need to shift
        allQuestions.forEach((q) => {
          if (q.id === question.id) return;

          // If moving up (old > new), shift questions between new and old down
          if (
            oldOrder > newOrder &&
            q.order >= newOrder &&
            q.order < oldOrder
          ) {
            reorderData.push({ id: q.id, order: q.order + 1 });
          }
          // If moving down (old < new), shift questions between old and new up
          else if (
            oldOrder < newOrder &&
            q.order > oldOrder &&
            q.order <= newOrder
          ) {
            reorderData.push({ id: q.id, order: q.order - 1 });
          }
        });

        // Call reorder endpoint
        await questionApi.reorder(reorderData);

        // Update other fields if any changed
        const { order: _, ...otherData } = data;
        if (Object.keys(otherData).length > 0) {
          await questionApi.update(question.id, otherData);
        }
      } else {
        // No order change, just update normally
        await questionApi.update(question.id, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['section', question.questionnaire_section_id],
      });
      queryClient.invalidateQueries({ queryKey: ['question', question.id] });
      router.reload({ only: ['section', 'template'] });
      onClose();
      toast.success('Question updated successfully');
    },
    onError: (error) =>
      handleQueryError(error, 'Failed to update question. Please try again.'),
  });

  const handleSubmit = (data: QuestionFormData) => {
    updateQuestionMutation.mutate(data);
  };

  return {
    updateQueryMutation: updateQuestionMutation,
    handleSubmit,
  };
}
